// Lightweight rich text editor using contentEditable + document.execCommand.
// Same underlying approach as the Gmail signature editor already in the app.
// Deliberately minimal — bold, italic, underline, unordered list, link.

import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, Link as LinkIcon } from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder, disabled, testId }) {
  const ref = useRef(null);
  const skipSyncRef = useRef(false);

  // Sync incoming value → DOM only when it differs (prevents caret jumps)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value || "";
    if (el.innerHTML !== incoming && !skipSyncRef.current) {
      el.innerHTML = incoming;
    }
    skipSyncRef.current = false;
  }, [value]);

  const emit = () => {
    skipSyncRef.current = true;
    onChange && onChange(ref.current?.innerHTML || "");
  };

  const cmd = (command) => (e) => {
    e.preventDefault();
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command);
    emit();
  };

  const insertLink = (e) => {
    e.preventDefault();
    if (disabled) return;
    const url = window.prompt("Link URL");
    if (!url) return;
    ref.current?.focus();
    document.execCommand("createLink", false, url);
    emit();
  };

  return (
    <div
      style={{
        border: "1px solid var(--pg-border)",
        borderRadius: 8,
        background: disabled ? "transparent" : "var(--pg-surface, transparent)",
      }}
      data-testid={testId}
    >
      {!disabled && (
        <div
          className="flex items-center gap-1 px-2 py-1"
          style={{ borderBottom: "1px solid var(--pg-border)" }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <ToolbarBtn onClick={cmd("bold")}      icon={<Bold size={13} />}   label="Bold" />
          <ToolbarBtn onClick={cmd("italic")}    icon={<Italic size={13} />} label="Italic" />
          <ToolbarBtn onClick={cmd("underline")} icon={<Underline size={13} />} label="Underline" />
          <div style={{ width: 1, height: 16, background: "var(--pg-border)", margin: "0 4px" }} />
          <ToolbarBtn onClick={cmd("insertUnorderedList")} icon={<List size={13} />} label="Bullet list" />
          <ToolbarBtn onClick={insertLink} icon={<LinkIcon size={13} />} label="Insert link" />
        </div>
      )}
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={(e) => {
          // Force plain text paste to avoid dragging in someone else's styles
          e.preventDefault();
          const text = (e.clipboardData || window.clipboardData).getData("text/plain");
          document.execCommand("insertText", false, text);
        }}
        data-placeholder={placeholder || ""}
        className="pg-richtext"
        style={{
          minHeight: 100, padding: "10px 12px",
          outline: "none",
          fontSize: 14, lineHeight: 1.6,
          color: "var(--pg-text)",
        }}
      />
    </div>
  );
}

function ToolbarBtn({ onClick, icon, label }) {
  return (
    <button
      type="button"
      onMouseDown={onClick}
      title={label}
      className="pg-btn pg-btn-ghost"
      style={{ padding: 5, fontSize: 12 }}
    >
      {icon}
    </button>
  );
}
